import { useMemo } from 'react';

import type {
  DATypeConfigInfo,
  DATypeConfigInfoDamageAssessmentConfigSchema,
} from '~/server_shared/openapi/data-contracts';

import { useClaim } from '../ClaimContainer';
import type { ExposureModel } from '../types/exposure-types';
import useDataFetcher from '../useDataFetcher';

export type DamageAssessmentConfig = DATypeConfigInfoDamageAssessmentConfigSchema;

export type InvoiceConfig = ExposureModel['invoice_config'];

interface UseDamageAssessmentConfigReturn {
  daTypeConfig: DamageAssessmentConfig | null;
  invoiceConfig: InvoiceConfig;
  isLoading: boolean;
}

/**
 * Resolves the DamageAssessmentConfig and InvoiceConfig for an exposure.
 *
 * daTypeConfig: resolved from exposure.damage_assessment_type (string key) -> org's DA type configs -> match by key.
 * invoiceConfig: read directly from exposure.invoice_config (serialized in claim_for_fe.py).
 *
 * Returns null when configs are not assigned (null = Fast Cover default behavior).
 */
export const useDamageAssessmentConfig = (exposure: ExposureModel): UseDamageAssessmentConfigReturn => {
  const { claim } = useClaim({ claimRequired: true });
  const daTypeKey = exposure.damage_assessment_type;

  // Only fetch configs if the exposure has a DA type assigned
  const url = daTypeKey ? `/api/v1/organizations/${claim.organization_id}/damage_assessment_type_configs` : null;

  const { isLoading, data: daTypeConfigs } = useDataFetcher<DATypeConfigInfo[]>(url);

  const daTypeConfig = useMemo((): DamageAssessmentConfig | null => {
    if (!daTypeKey || !daTypeConfigs) return null;

    const matchingConfig = daTypeConfigs.find((c) => c.key === daTypeKey && !c.is_disabled);
    if (!matchingConfig) return null;

    return matchingConfig.config;
  }, [daTypeKey, daTypeConfigs]);

  // Invoice config is already on the exposure response — no separate API call needed
  const invoiceConfig = exposure.invoice_config ?? null;

  return { daTypeConfig, invoiceConfig, isLoading };
};
